import { useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export interface UnifiedNotification {
  id: number | string;
  created_at?: string;
  createdAt?: string;
  type?: string;
  source?: string;
  severity?: string;
  title?: string;
  message?: string;
  payload?: Record<string, unknown> | null;
  read?: boolean;
}

interface NotificationsPanelProps {
  items: UnifiedNotification[];
  loading?: boolean;
  onRefresh?: () => void;
}

export function NotificationsPanel({ items, loading, onRefresh }: NotificationsPanelProps) {
  const handleMarkRead = useCallback(
    async (id: number | string) => {
      try {
        await fetch(`/notifications/${id}/read`, {
          method: "POST",
          credentials: "include",
        });
        onRefresh?.();
      } catch (error) {
        // swallow for now; UI will retry on next poll
        console.error("Failed to mark notification read", error);
      }
    },
    [onRefresh],
  );

  return (
    <Card className="w-full max-w-sm border-border/80 bg-background/60">
      <div className="border-b border-border/70 px-4 py-3">
        <p className="text-sm font-semibold text-foreground">Live Alerts</p>
        <p className="text-xs text-muted-foreground">Unified notifications from every agent.</p>
      </div>
      <div className="max-h-[600px] space-y-3 overflow-y-auto px-4 py-3">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading notifications…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">No alerts yet.</p>
        ) : (
          items.map((item) => {
            const createdAt = item.created_at ?? item.createdAt;
            const timestamp = createdAt ? new Date(createdAt).toLocaleString() : "";
            const payloadPreview =
              typeof item.payload?.preview === "string"
                ? item.payload.preview
                : typeof item.payload === "object" && item.payload
                  ? JSON.stringify(item.payload)
                  : null;

            return (
              <div
                key={item.id}
                className={cn(
                  "rounded-xl border border-border/70 bg-background/30 p-3 shadow-sm",
                  !item.read && "border-primary/60",
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-xs text-muted-foreground">{timestamp}</p>
                    <p className="text-sm font-semibold text-foreground">{item.title ?? "Notification"}</p>
                  </div>
                  <Badge
                    variant="secondary"
                    className={cn(
                      "text-xs uppercase",
                      item.severity === "critical" && "border-destructive/50 bg-destructive/10 text-destructive",
                      item.severity === "warning" && "border-warning/50 bg-warning/10 text-warning",
                      item.severity === "info" && "border-info/50 bg-info/10 text-info",
                    )}
                  >
                    {item.severity ?? "info"}
                  </Badge>
                </div>
                {item.message ? <p className="mt-2 text-sm text-foreground/70">{item.message}</p> : null}
                {payloadPreview ? (
                  <pre className="mt-2 overflow-hidden truncate rounded-lg bg-background/60 p-2 text-xs text-muted-foreground">
                    {payloadPreview}
                  </pre>
                ) : null}
                <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                  <span>{item.source ?? "system"}</span>
                  {!item.read ? (
                    <Button variant="ghost" size="sm" className="text-primary" onClick={() => void handleMarkRead(item.id)}>
                      Mark read
                    </Button>
                  ) : (
                    <span className="text-muted-foreground">Read</span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </Card>
  );
}
